// Lesson manifest + body loader. The tutorial mode fetches the
// manifest once on boot, then lazy-loads each lesson body when the
// user opens it. Manifest is JSON, lesson body is markdown with
// fenced sections that the parser splits below.
//
// Lesson body format:
//
//   # Title
//
//   Free-form markdown explanation goes here. Inline `code`, **bold**,
//   and fenced ```sas``` blocks are all rendered.
//
//   --- starter
//   proc sql;
//       create table src ...;
//   quit;
//   --- expected.csv
//   col1,col2
//   1,A
//   2,B
//   --- target
//   work.refdiff_out
//
// Sections after the body are introduced by a `--- name` line at the
// start of a line. The known sections are `starter`, `expected.csv`,
// `expected.log`, and `target`. Anything else is ignored.

import { renderMarkdown } from './markdown';
import {
  parseExpectedCsv,
  queryResultAsCsv,
  compareCsv,
  type CheckResult,
} from './checker';
import { runQuery } from '../duckdb';

export interface LessonRef {
  id: string;
  title: string;
  file: string;
}

export interface SliceManifest {
  id: string;
  title: string;
  lessons: LessonRef[];
}

export interface Manifest {
  version: number;
  slices: SliceManifest[];
}

export interface Lesson {
  id: string;
  title: string;
  /** Pre-rendered HTML for the lesson explanation. */
  bodyHtml: string;
  /** SAS source preloaded into the editor. */
  starter: string;
  /** Optional expected CSV (raw text) to compare against. */
  expectedCsv?: string;
  /** Optional WORK target table; query is `SELECT * FROM <target> ORDER BY 1`. */
  target?: string;
}

const MANIFEST_URL = 'lessons/manifest.json';

let manifestPromise: Promise<Manifest> | null = null;

export function loadManifest(): Promise<Manifest> {
  if (!manifestPromise) {
    manifestPromise = fetch(MANIFEST_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`manifest: ${r.status} ${r.statusText}`);
        return r.json() as Promise<Manifest>;
      });
  }
  return manifestPromise;
}

const lessonCache = new Map<string, Promise<Lesson>>();

export function loadLesson(ref: LessonRef): Promise<Lesson> {
  const cached = lessonCache.get(ref.id);
  if (cached) return cached;
  const loaded = (async (): Promise<Lesson> => {
    const r = await fetch(`lessons/${ref.file}`);
    if (!r.ok) throw new Error(`lesson '${ref.id}': ${r.status} ${r.statusText}`);
    const text = await r.text();
    return parseLesson(ref, text);
  })();
  lessonCache.set(ref.id, loaded);
  return loaded;
}

function parseLesson(ref: LessonRef, text: string): Lesson {
  // Sections are introduced by a line starting with `--- name`. The
  // body before the first section is the markdown explanation; each
  // section's content is everything up to the next `--- name` line.
  const lines = text.split('\n');
  const sections: Record<string, string[]> = { body: [] };
  let current = 'body';
  for (const line of lines) {
    const m = /^---\s+(\S+)\s*$/.exec(line);
    if (m) {
      current = m[1];
      sections[current] = [];
      continue;
    }
    sections[current].push(line);
  }

  const bodyMd = (sections.body ?? []).join('\n').trim();
  const starter = sections.starter ? sections.starter.join('\n').trim() : '';
  const expectedCsv = sections['expected.csv']
    ? sections['expected.csv'].join('\n').trim()
    : undefined;
  const target = sections.target ? sections.target.join('\n').trim() : undefined;

  return {
    id: ref.id,
    title: ref.title,
    bodyHtml: renderMarkdown(bodyMd),
    starter,
    expectedCsv,
    target,
  };
}

/// After the user clicks "Run" + "Check", query the WORK target and
/// diff its rows against `expectedCsv` using the same CSV-comparison
/// logic the native refdiff harness uses.
export async function checkLesson(lesson: Lesson): Promise<CheckResult | null> {
  if (!lesson.expectedCsv || !lesson.target) return null;
  const expected = parseExpectedCsv(lesson.expectedCsv);
  // ORDER BY 1 keeps row order stable so the diff isn't sensitive to
  // DuckDB's storage order (DATA-step OUTPUT preserves input order in
  // kolilang native; PIVOT and other PROCs may not).
  const sql = `SELECT * FROM ${quoteTarget(lesson.target)} ORDER BY 1`;
  const qr = await runQuery(sql);
  return compareCsv(expected, queryResultAsCsv(qr));
}

function quoteTarget(target: string): string {
  // Accept `name`, `schema.name`, or already-quoted `"schema"."name"`.
  if (target.includes('"')) return target;
  return target.split('.').map((p) => `"${p.replace(/"/g, '""')}"`).join('.');
}
